<template>
  <v-card class="calendar-card" elevation="12">
    <div class="calendar-area">
      <FullCalendar 
        ref="fullCalendar" 
        :options="calendarOptions"
      />
    </div>
  </v-card>

  <BookingDialog
    ref="bookingDialogRef"
    :bookingDialogStartDate="clickedStartDate || calendarStartDate"
    :bookingDialogEndDate="clickedEndDate || calendarEndDate"
    :bookingDialogSeat="calendarSeat"
    :bookingDialogUsers="calendarUsers"
    @updateFavorite="$emit('updateFavorite')"
    @updateSeatStatus="refreshOccupancy"
  />
</template>

<script>
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import BookingDialog from '../components/BookingDialog.vue'
import { nextTick } from 'vue'

export default {
  name: 'CalendarCard',
  components: { FullCalendar, BookingDialog },

  props: {
    calendarSeat: { type: Object, default: null },
    calendarUsers: { type: Object, default: null },
    calendarSeatOwner: { type: String, default: '' }
  },

  emits: ['updateFavorite', 'updateSeatStatus'],

  data() {
    return {
      occupancy: [],
      releaseDates: [],
      clickedStartDate: '',
      clickedEndDate: '',
      calendarStartDate: '',
      calendarEndDate: '',

      calendarOptions: {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        locale: 'de',
        firstDay: 1,
        fixedWeekCount: true,
        expandRows: true,
        contentHeight: 'auto',
        headerToolbar: { left: 'title', center: '', right: 'prev next' },
        dayMaxEvents: 1,
        events: [],
        dateClick: null,
        datesSet: null,

        eventDidMount(info) {
          info.el.setAttribute('title', info.event?.title || '')
        }
      }
    }
  },

  created() {
    this.calendarOptions.dateClick = this.handleDateClick
    this.calendarOptions.datesSet = this.handleDatesSet
  },

  watch: {
    calendarSeat() {
      this.refreshOccupancy()
    }
  },

  methods: {

    expandReservationItems(resItems) {
      const days = []

      resItems.forEach(item => {
        let d = new Date(item.start)
        const end = new Date(item.end)

        while (d <= end) {
          days.push(d.toISOString().split("T")[0])
          d.setDate(d.getDate() + 1)
        }
      })

      return days
    },

    expandAllReservations(reservations) {
      const out = new Set()

      reservations.forEach(res => {
        const expanded = this.expandReservationItems(res.reservation_items)
        expanded.forEach(d => out.add(d))
      })

      return [...out].sort()
    },

    calendarEvents() {
      const events = []
      const users = this.calendarUsers?.users || []

      const todayStr = new Date().toISOString().split("T")[0]

      // 1) Buchungen mit expandierten Tagen
      this.occupancy.forEach(res => {
        const user = users.find(u => u.id === res.reserved_for)
        const username = user ? `${user.first_name} ${user.last_name}` : 'Unbekannt'

        const reservedDays = this.expandReservationItems(res.reservation_items)

        reservedDays.forEach(dayStr => {
          if (dayStr < todayStr) return

          events.push({
            title: username,
            date: dayStr,
            color: 'rgb(255, 124, 2)',
            editable: false
          })
        })
      })

      // 2) Owner-Block
      if (this.calendarSeat?.owner) {
        const allDays = this.generateDayRange(this.calendarStartDate, this.calendarEndDate)

        allDays.forEach(dayStr => {
          const isReleased = this.releaseDates.includes(dayStr)
          const hasRes = events.some(e => e.date === dayStr)

          if (!isReleased && !hasRes) {
            events.push({
              title: `${this.calendarSeatOwner}`,
              date: dayStr,
              color: 'grey',
              editable: false
            })
          }
        })
      }

      return events
    },

    generateDayRange(startStr, endStr) {
      const out = []
      let d = new Date(startStr)
      const end = new Date(endStr)

      while (d <= end) {
        out.push(d.toISOString().split("T")[0])
        d.setDate(d.getDate() + 1)
      }
      return out
    },

    async refreshOccupancy() {
      await this.loadReleaseTimes()
      await this.loadOccupancy()
      this.$emit('updateSeatStatus')
    },

    async loadReleaseTimes() {
      if (!this.calendarSeat?.owner) {
        this.releaseDates = []
        return
      }
      try {
        const { data } = await this.$axios.get(`/api/owner-release-items/`, {
          params: { seat: this.calendarSeat.id }
        })
        this.releaseDates = data.map(d => d.date)
      } catch (err) {
        console.error('Fehler beim Laden der Freigaben:', err)
      }
    },

    async loadOccupancy() {
      const api = this.$refs.fullCalendar?.getApi?.()

      if (!this.calendarSeat) {
        api?.removeAllEvents()
        this.occupancy = []
        return
      }

      try {
        const { data } = await this.$axios.get(
          `/api/reservations/${this.calendarSeat.id}/bySeat/`,
          { params: { start: this.calendarStartDate, end: this.calendarEndDate } }
        )

        this.occupancy = data
        await nextTick()

        const api = this.$refs.fullCalendar.getApi()
        api.removeAllEvents()
        api.addEventSource(this.calendarEvents())
        api.refetchEvents()

      } catch (err) {
        console.error('Fehler beim Laden der Belegung:', err)
      }
    },

    handleDatesSet(dates) {
      this.calendarStartDate = dates.startStr.split("T")[0]

      const end = new Date(dates.endStr)
      end.setDate(end.getDate() - 1)
      this.calendarEndDate = end.toISOString().split("T")[0]

      if (this.calendarSeat) this.refreshOccupancy()
    },

    handleDateClick(info) {
      if (!this.calendarSeat || !this.calendarUsers) return

      const clicked = info.dateStr
      const dayObj = new Date(clicked)

      const today = new Date()
      today.setHours(0,0,0,0)

      const maxDate = new Date()
      maxDate.setMonth(maxDate.getMonth() + 3)

      const clickedDate = new Date(clicked)
      if (clickedDate < today || clickedDate > maxDate) return

      const day = dayObj.getDay()

      // Regeln:
      if (dayObj < today) return
      if (day === 0 || day === 6) return

      const reservedDays = new Set(this.expandAllReservations(this.occupancy))

      const isReserved = reservedDays.has(clicked)
      const isReleased = this.releaseDates.includes(clicked)

      if (this.calendarSeat.owner) {
        if (!isReleased || isReserved) return
      } else {
        if (isReserved) return
      }

      this.clickedStartDate = clicked
      this.clickedEndDate = clicked
      nextTick(() => this.showDialogBooking())
    },

    showDialogBooking() {
      this.$refs.bookingDialogRef?.openDialogBooking()
    }
  }
}
</script>

<style scoped>

.calendar-card {
  font-size: clamp(16px, 0.9rem + 0.4vw, 18px);
  line-height: 1.4;
  padding: 1rem;
  height: 100%;
  min-width: 0;
}
.calendar-area {
  min-height: 0;
  height: 100%;
  width: 100%;
  min-width: 0; 
}

:deep(.fc), 
:deep(.fc *){min-width: 0;}

:deep(.fc .fc-scrollgrid){width: 100% !important; box-sizing: border-box !important; }
:deep(.fc .fc-scrollgrid table){width: 100% !important;table-layout: fixed !important;border-collapse: collapse;}

:deep(.fc .fc-col-header),
:deep(.fc .fc-daygrid-body),
:deep(.fc .fc-daygrid-body table){width: 100% !important; height: 100% !important;}

:deep(.fc .fc-toolbar) {font-size: 0.75rem !important;}
:deep(.fc .fc-button) {background-color: rgb(100, 100, 100); border-color: rgb(100, 100, 100);} 

:deep(.fc .fc-daygrid-day){min-width: 0 !important; height: 6.1rem; cursor: grab;}
:deep(.fc-daygrid-day:hover) { background-color: rgba(255,115,0,0.08); }
:deep(.fc-daygrid-day.clicked) { background-color: rgba(0,0,0,0.15) !important; transition: background-color .3s; }

:deep(.fc-daygrid-day.fc-day-past .fc-daygrid-day-frame) {background-color: rgb(243, 243, 243); cursor: not-allowed !important;}
:deep(.fc-daygrid-day.fc-day-sat .fc-daygrid-day-frame) {background-color: rgba(247, 247, 247, 0.95); cursor: not-allowed !important;}
:deep(.fc-daygrid-day.fc-day-sun .fc-daygrid-day-frame) {background-color: rgba(247, 247, 247, 0.95); cursor: not-allowed !important;}

:deep(.fc-daygrid-day.fc-day-past .fc-daygrid-day-number) {color: rgb(165, 165, 165) !important; opacity: 0.95 !important;}
:deep(.fc-daygrid-day.fc-day-sat .fc-daygrid-day-number) {color: rgb(165, 165, 165) !important; opacity: 0.95 !important;}
:deep(.fc-daygrid-day.fc-day-sun .fc-daygrid-day-number) {color: rgb(165, 165, 165) !important; opacity: 0.95 !important;}

:deep(.fc-event) {background-color: rgb(255, 124, 2); border-color: rgb(255, 124, 2); height: 100% !important}
:deep(.fc-event-title) {white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: .9rem; font-size:large;}

/* Vergangene Tage & Wochenende */
.v-theme--dark :deep(.fc-col-header) {color: rgb(190, 190, 190);}
.v-theme--dark :deep(.fc-col-header-cell) {border-color: rgba(255,255,255, 0.332);}

.v-theme--dark :deep(.fc-scrollgrid) {border-color: rgba(255, 255, 255, 0.322);}
.v-theme--dark :deep(.fc-scrollgrid td) {border-color: rgba(255, 255, 255, 0.322);}
.v-theme--dark :deep(.fc-scrollgrid tbody) {border-color: rgba(255, 255, 255, 0.322);}
.v-theme--dark :deep(.fc-scrollgrid thead) {border-color: rgba(255, 255, 255, 0.322);}

.v-theme--dark :deep(.fc-daygrid-body) {color: rgb(255, 255, 255);}

.v-theme--dark :deep(.fc-daygrid-day:hover) { background-color: rgba(255, 255, 255, 0.11); }

.v-theme--dark :deep(.fc-daygrid-day.fc-day-past .fc-daygrid-day-frame) {background-color: rgba(128, 128, 128, 0.3) ;cursor: not-allowed !important;}
.v-theme--dark :deep(.fc-daygrid-day.fc-day-sat .fc-daygrid-day-frame) {background-color: rgba(85, 85, 85, 0.3) ;cursor: not-allowed !important;}
.v-theme--dark :deep(.fc-daygrid-day.fc-day-sun .fc-daygrid-day-frame) {background-color: rgba(85, 85, 85,0.3) ;cursor: not-allowed !important;}

.v-theme--dark :deep(.fc-daygrid-day.fc-day-past .fc-daygrid-day-number) {color: rgb(165, 165, 165) !important; opacity: 0.95 !important;}
.v-theme--dark :deep(.fc-daygrid-day.fc-day-sat .fc-daygrid-day-number) {color: rgb(165, 165, 165) !important; opacity: 0.95 !important;}
.v-theme--dark :deep(.fc-daygrid-day.fc-day-sun .fc-daygrid-day-number) {color: rgb(165, 165, 165) !important; opacity: 0.95 !important;}


</style>
